import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, X, ChevronLeft } from 'lucide-react';
import { Prayer } from '@/types/prayer';
import { LAYERS } from '@/utils/layers';
import { GestureOverlay, GestureHint } from '@/components/shared/GestureOverlay';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';
import GlobalAudioPlayerControl from '@/components/audio/GlobalAudioPlayerControl';
import { LanguageSelector } from '@/components/audio/LanguageSelector';

type LanguageMode = 'english' | 'chinese' | 'both';

interface DialogHeaderProps {
  /** Visual style of the header */
  variant: 'audio' | 'regular' | 'primary' | 'minimal';
  /** Expand/collapse state from parent */
  isExpanded: boolean;
  /** Toggle expand/collapse */
  onToggle: () => void;
  /** Close dialog */
  onClose: () => void;

  /** Show swipe/tap gesture hints over the header area */
  showHints?: boolean;

  /** Optional mobile flag; if omitted we infer by viewport width */
  isMobile?: boolean;

  /** Optional prayer (for audio variant language selector) */
  prayer?: Prayer;

  /** Optional swipe state object (for gesture overlays) */
  swipeState?: unknown;

  /** Primary variant: go back callback */
  onBackClick?: () => void;

  /** Primary/regular: custom left slot */
  customLeftSection?: React.ReactNode;

  /** Primary/regular: custom right slot */
  customRightSection?: React.ReactNode;

  /** If rendering within a dialog “frame” component */
  inDialogFrame?: boolean;

  /** If a parent frame manages its own z-index band */
  dialogFrameZIndex?: number;

  /** Force a specific z-index for header if needed */
  customZIndex?: number;
}

/**
 * DialogHeader
 * - 5-section grid layout (48px | 1fr | 48px | 1fr | 48px)
 * - Safe-area aware sticky positioning (except when inDialogFrame=true)
 * - Z-index aligned with LAYERS.DIALOG_HEADER and LAYERS.DIALOG_GESTURES
 * - Variants: primary (back + slots), audio (audio UI + language), minimal (just close), regular (toggle + close)
 */
export default function DialogHeader({
  variant,
  isExpanded,
  onToggle,
  onClose,
  showHints = false,
  isMobile: propIsMobile,
  prayer,
  onBackClick,
  customLeftSection,
  customRightSection,
  inDialogFrame = false,
  dialogFrameZIndex,
  customZIndex,
}: DialogHeaderProps) {
  // ====== State / derived ======
  const [showGestureHints, setShowGestureHints] = useState(showHints);

  // Mobile detection if not provided
  const isMobile = useMemo(() => {
    if (typeof propIsMobile === 'boolean') return propIsMobile;
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768;
  }, [propIsMobile]);

  // ====== Audio integration ======
  const { stop, currentLanguage, setLanguage } = useAbielAudio();

  // ====== Gesture hint auto-hide ======
  useEffect(() => {
    if (!showHints) {
      setShowGestureHints(false);
      return;
    }
    setShowGestureHints(true);
    const t = setTimeout(() => setShowGestureHints(false), 6000);
    return () => clearTimeout(t);
  }, [showHints]);

  const handleCloseAndStopIfAudio = () => {
    if (variant === 'audio') stop();
    onClose();
  };

  const handleLanguageChange = (mode: LanguageMode) => {
    // Keep for debug visibility; safe in production (removable)
    console.log('🌐 Language changed:', mode);
    setLanguage(mode);
  };

  // ====== Z-index + base classes ======
  const headerZIndex = customZIndex ?? dialogFrameZIndex ?? LAYERS.DIALOG_HEADER;

  // If in a dialog frame, the frame might manage position, so keep header relative.
  // Otherwise, make it sticky and respect iOS safe area.
  const containerBase =
    inDialogFrame
      ? 'relative bg-golden-gradient dark:bg-background border-b border-border/50'
      : 'sticky bg-golden-gradient dark:bg-background border-b border-border/50';

  // Rounded tops for mobile; subtle rounding for desktop
  const rounded =
    isMobile ? 'rounded-t-2xl -mt-1' : 'rounded-t-lg';

  const stickyTop = inDialogFrame ? '' : 'top-0';

  const containerClasses = `${containerBase} ${rounded} ${stickyTop}`;

  const containerStyle: React.CSSProperties = {
    zIndex: headerZIndex,
  };

  // Reusable grid values
  const gridColumns = '48px 1fr 48px 1fr 48px';
  const minHeaderHeight = '56px';

  // ====== Common toggle button (center slot) ======
  const ToggleButton = (
    <button
      onClick={onToggle}
      className="flex items-center justify-center p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold hover:bg-white/10 dark:hover:bg-black/20"
      aria-label={isExpanded ? 'Collapse dialog' : 'Expand dialog'}
    >
      {isExpanded ? (
        <ChevronDown size={isMobile ? 20 : 24} className="text-gray-600 dark:text-gray-400" />
      ) : (
        <div className="w-12 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
      )}
    </button>
  );

  // ====== Close button (right slot) ======
  const CloseButton = (
    <button
      onClick={handleCloseAndStopIfAudio}
      className="p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold hover:bg-white/10 dark:hover:bg-black/20"
      aria-label="Close dialog"
    >
      <X size={isMobile ? 20 : 24} className="text-gray-600 dark:text-gray-400" />
    </button>
  );

  // ====== Primary: back on left, slots, toggle in center, slot, close right ======
  if (variant === 'primary') {
    return (
      <div className={containerClasses} style={containerStyle}>
        <div
          className="grid items-center px-2 gap-1"
          style={{ gridTemplateColumns: gridColumns, minHeight: minHeaderHeight }}
          role="toolbar"
          aria-label="Prayer dialog header"
        >
          {/* 1. Back */}
          <div className="flex justify-center items-center">
            <button
              onClick={onBackClick}
              className="p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold hover:bg-white/10 dark:hover:bg-black/20"
              aria-label="Go back"
            >
              <ChevronLeft size={20} className="text-gray-400 dark:text-gray-600" />
            </button>
          </div>

          {/* 2. Custom left slot */}
          <div className="flex justify-center items-center">
            {customLeftSection}
          </div>

          {/* 3. Toggle */}
          <div className="flex justify-center items-center">
            {ToggleButton}
          </div>

          {/* 4. Custom right slot */}
          <div className="flex justify-center items-center">
            {customRightSection}
          </div>

          {/* 5. Close */}
          <div className="flex justify-center items-center">
            {CloseButton}
          </div>
        </div>

        <GestureOverlay zIndex={LAYERS.DIALOG_GESTURES}>
          <GestureHint
            message={isExpanded ? 'Tap to collapse or swipe down' : 'Tap to expand or swipe up'}
            isVisible={showGestureHints}
          />
        </GestureOverlay>
      </div>
    );
  }

  // ====== Audio: global audio controls on left/left-center, toggle center, language right-center, close right ======
  if (variant === 'audio') {
    return (
      <div className={containerClasses} style={containerStyle}>
        <div
          className="grid items-center px-2 py-2 gap-1"
          style={{ gridTemplateColumns: gridColumns, minHeight: minHeaderHeight }}
          role="toolbar"
          aria-label="Audio dialog header"
        >
          {/* 1. Global audio icon control */}
          <div className="flex justify-center items-center">
            <GlobalAudioPlayerControl controlType="icon-only" variant="header" />
          </div>

          {/* 2. Global audio indicator/progress */}
          <div className="flex justify-center items-center px-1">
            <GlobalAudioPlayerControl controlType="indicator-only" variant="header" className="max-w-full" />
          </div>

          {/* 3. Toggle */}
          <div className="flex justify-center items-center">
            {ToggleButton}
          </div>

          {/* 4. Language selector (if prayer content is present) */}
          <div className="flex justify-center items-center px-1">
            {prayer && (
              <LanguageSelector
                selectedLanguage={currentLanguage}
                onLanguageChange={handleLanguageChange}
                variant="compact"
                disabled={false}
                contentText={prayer.content}
              />
            )}
          </div>

          {/* 5. Close */}
          <div className="flex justify-center items-center">
            {CloseButton}
          </div>
        </div>

        <GestureOverlay zIndex={LAYERS.DIALOG_GESTURES}>
          <GestureHint
            message={isExpanded ? 'Tap to collapse or swipe down' : 'Tap to expand or swipe up'}
            isVisible={showGestureHints}
          />
        </GestureOverlay>
      </div>
    );
  }

  // ====== Minimal: just a close button on the far right; others empty to preserve centering grid ======
  if (variant === 'minimal') {
    return (
      <div className={containerClasses} style={containerStyle}>
        <div
          className="grid items-center px-2 py-2 gap-1"
          style={{ gridTemplateColumns: gridColumns, minHeight: minHeaderHeight }}
          role="toolbar"
          aria-label="Compact dialog header"
        >
          <div className="flex justify-center items-center" />
          <div className="flex justify-center items-center" />
          <div className="flex justify-center items-center" />
          <div className="flex justify-center items-center" />
          <div className="flex justify-center items-center">
            {CloseButton}
          </div>
        </div>

        <GestureOverlay zIndex={LAYERS.DIALOG_GESTURES}>
          <GestureHint message="Swipe down to close" isVisible={showGestureHints} />
        </GestureOverlay>
      </div>
    );
  }

  // ====== Regular: center toggle + right close; empty flexible slots for symmetry ======
  return (
    <div className={containerClasses} style={containerStyle}>
      <header
        role="toolbar"
        className="grid items-center px-2 py-2 gap-1"
        style={{ gridTemplateColumns: gridColumns, minHeight: minHeaderHeight, alignItems: 'center' }}
        aria-label="Dialog header"
      >
        {/* 1. Empty */}
        <div className="flex justify-center items-center" />
        {/* 2. Empty */}
        <div className="flex justify-center items-center" />
        {/* 3. Toggle */}
        <div className="flex justify-center items-center">
          {ToggleButton}
        </div>
        {/* 4. Empty */}
        <div className="flex justify-center items-center" />
        {/* 5. Close */}
        <div className="flex justify-center items-center">
          <button
            onClick={onClose}
            className="flex items-center justify-center p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-gold hover:bg-white/10 dark:hover:bg-black/20"
            aria-label="Close dialog"
          >
            <X size={isMobile ? 20 : 24} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>
      </header>

      <GestureOverlay zIndex={LAYERS.DIALOG_GESTURES}>
        <GestureHint
          message={isExpanded ? 'Tap to collapse or swipe down' : 'Tap to expand or swipe up'}
          isVisible={showGestureHints}
          position="bottom"
          className="transition-opacity duration-1000"
        />
      </GestureOverlay>
    </div>
  );
}
